import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../Contexts/LoginContext";
import { RoleContext } from "../Contexts/RoleContext";
import logo from '../static/Crunch Time Jobs.png';
import background from '../static/login_background.jpg';
import '../static/user.css';
import { url } from "../Components/url";
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { setCsrfToken } from "../Components/csrf";
import { Spinner } from "react-bootstrap";

function MfaVerify() {
    const { setUsername } = useContext(UserContext);
    const { setRole } = useContext(RoleContext);
    const navigate = useNavigate();
    const location = useLocation();
    const { requires_mfa: requiresMfa, userId } = location.state || {};

    const [method, setMethod] = useState('authenticator');
    const [code, setCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const methods = location.state?.methods || ['authenticator', 'email', 'backup'];

    useEffect(() => {
        if (!requiresMfa) {
            navigate('/', { replace: true });
        }
    }, [requiresMfa, navigate]);

    const handleSendEmailCode = async () => {
        setErrorMessage('');
        try {
            setSendingEmail(true);
            const response = await fetch(`${url}/mfa/send-email-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({}),
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                setErrorMessage('');
                setCode('');
            } else {
                setErrorMessage(data.message || 'Failed to send code');
            }
        } catch (err) {
            setErrorMessage('Failed to send code');
        } finally {
            setSendingEmail(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        try {
            setIsLoading(true);
            const response = await fetch(`${url}/mfa/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    method,
                    code: code.replace(/\s/g, ''),
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                if (data.csrf_token) {
                    setCsrfToken(data.csrf_token);
                }
                const uid = data.id ?? userId;
                setUsername(uid);
                const res = await fetchWithAuth(`${url}/get_user/${uid}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                });
                if (!res.ok) throw new Error('Failed to load user');
                const userData = await res.json();
                setRole(userData.role);
                if (userData.role === 'admin') navigate('/home');
                else if (['Counsellor', 'Visa Counsellor', 'Visa&Application Counsellor'].includes(userData.role)) navigate('/new-enquiry');
                else if (userData.role === 'Receptionist') navigate('/all-enquiry');
                else if (userData.role === 'Manager') navigate('/view-applications');
                else navigate('/home');
            } else {
                setErrorMessage(data.message || 'Verification failed');
            }
        } catch (err) {
            setErrorMessage(err.message || 'Verification failed');
        } finally {
            setIsLoading(false);
        }
    };

    if (!requiresMfa) return null;

    return (
        <div className="login-container">
            <div className="login-background">
                <img src={background} alt="Background" />
            </div>
            <div className="login-content">
                <div className="login-card">
                    <div className="login-header">
                        <img src={logo} alt="Logo" className="login-logo" />
                        <h1 className="login-title">Two-factor authentication</h1>
                        <p className="login-subtitle">Enter the code from your chosen method</p>
                    </div>
                    <form onSubmit={handleSubmit} className="login-body">
                        <div className="login-form-group">
                            <label className="login-form-label">Method</label>
                            <select
                                className="login-form-control"
                                value={method}
                                onChange={(e) => setMethod(e.target.value)}
                            >
                                {methods.includes('authenticator') && (
                                    <option value="authenticator">Authenticator app (e.g. Microsoft Authenticator)</option>
                                )}
                                {methods.includes('email') && (
                                    <option value="email">Email code</option>
                                )}
                                {methods.includes('backup') && (
                                    <option value="backup">Backup code</option>
                                )}
                            </select>
                        </div>
                        {method === 'email' && (
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm mb-2"
                                onClick={handleSendEmailCode}
                                disabled={sendingEmail}
                            >
                                {sendingEmail ? <Spinner animation="border" size="sm" /> : 'Send code to my email'}
                            </button>
                        )}
                        <div className="login-form-group">
                            <label htmlFor="mfa-code" className="login-form-label">Code</label>
                            <input
                                type="text"
                                id="mfa-code"
                                className="login-form-control"
                                placeholder={method === 'backup' ? 'Enter backup code' : 'Enter 6-digit code'}
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                required
                                autoComplete="one-time-code"
                            />
                        </div>
                        {errorMessage && (
                            <div className="login-error d-flex align-items-center mt-2 text-danger" style={{ fontSize: '0.875rem' }}>
                                {errorMessage}
                            </div>
                        )}
                        <button type="submit" className="btn btn-primary mt-3" style={{ width: '100%', padding: '0.75rem' }}>
                            {isLoading ? <Spinner animation="border" size="sm" /> : 'Verify'}
                        </button>
                        <p className="mt-3 text-center">
                            <button type="button" className="btn btn-link p-0 login-link" onClick={() => navigate('/')}>
                                Back to sign in
                            </button>
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default MfaVerify;
